import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft, ArrowRight, Target, Hammer, BookMarked, MessagesSquare,
    Trophy, CheckCircle2, XCircle, Package, Youtube, Printer, FileDown,
    Flag, X, Send, Loader2,
} from "lucide-react";
import { fetchWeek, submitQuiz, submitVideoFeedback } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { exportWeekHandoutPdf } from "@/lib/pdf";

export default function WeekDetail() {
    const { id } = useParams();
    const { lang, t } = useLang();
    const [week, setWeek] = useState(null);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    useEffect(() => {
        setWeek(null);
        setAnswers({});
        setResult(null);
        fetchWeek(id, lang).then(setWeek).catch(() => setWeek("notfound"));
    }, [id, lang]);

    if (week === null) {
        return (
            <main className="max-w-4xl mx-auto px-5 md:px-8 py-24">
                <p className="font-mono text-[var(--muted)]">{t.common.loading}</p>
            </main>
        );
    }
    if (week === "notfound") {
        return (
            <main className="max-w-4xl mx-auto px-5 md:px-8 py-24">
                <h1 className="font-display text-3xl text-white">{t.week.notfound}</h1>
                <Link to="/curriculum" className="lnk mt-4 inline-block">
                    ← {t.week.back}
                </Link>
            </main>
        );
    }

    const handleSelect = (qIdx, optIdx) => {
        if (result) return;
        setAnswers((a) => ({ ...a, [qIdx]: optIdx }));
    };

    const handleSubmit = async () => {
        if (Object.keys(answers).length !== week.quiz.length) {
            alert(lang === "es" ? "Responde todas." : "Answer all questions first.");
            return;
        }
        setSubmitting(true);
        try {
            const ans = week.quiz.map((_, i) => answers[i]);
            const res = await submitQuiz(week.id, ans);
            setResult(res);
            const key = "flow_progress";
            const prog = JSON.parse(localStorage.getItem(key) || "{}");
            prog[week.id] = { score: res.score, total: res.total };
            localStorage.setItem(key, JSON.stringify(prog));
        } catch (e) {
            alert("Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    const nextWeek = week.week < 6 ? `w${week.week + 1}` : null;
    const prevWeek = week.week > 1 ? `w${week.week - 1}` : null;

    return (
        <main className="relative" data-testid={`week-detail-${week.id}`}>
            <div className="glow glow-cyan -top-20 right-0" />
            <div className="max-w-5xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-24 relative z-10">
                <div className="flex items-center justify-between flex-wrap gap-3 no-print">
                    <Link
                        to="/curriculum"
                        className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--cyan)] hover:text-[var(--lime)] inline-flex items-center gap-2"
                        data-testid="back-to-list"
                    >
                        <ArrowLeft size={14} /> {t.week.back}
                    </Link>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => exportWeekHandoutPdf(week, lang)}
                            data-testid="week-pdf"
                            className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--coral)] hover:text-[var(--lime)] inline-flex items-center gap-2"
                        >
                            <FileDown size={14} /> PDF
                        </button>
                        <button
                            onClick={() => window.print()}
                            data-testid="week-print"
                            className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--lime)] hover:text-[var(--cyan)] inline-flex items-center gap-2"
                        >
                            <Printer size={14} /> {t.week.print}
                        </button>
                    </div>
                </div>

                <div id="handout">
                    <div className="mt-6 flex items-center gap-3 flex-wrap">
                        <span
                            className="font-brush text-[var(--coral)] text-6xl md:text-7xl -rotate-3 inline-block"
                            aria-hidden
                        >
                            W{week.week}
                        </span>
                        <span className="font-mono text-xs uppercase tracking-[0.22em] text-[var(--muted)]">
                            {week.duration} · {t.week.module} 0{week.week} {t.week.of} 06
                        </span>
                    </div>

                    <h1 className="font-display font-black text-white text-4xl md:text-6xl mt-2 uppercase leading-[0.95]">
                        {week.title}
                    </h1>
                    <p className="font-mono text-[var(--muted)] mt-4 text-lg">
                        {week.tagline}
                    </p>

                    <div className="frame mt-8 no-print">
                        <img
                            src={week.image}
                            alt={week.title}
                            className="w-full h-[260px] md:h-[380px] object-cover"
                        />
                    </div>

                    {week.video && (
                        <Section Icon={Youtube} title={t.week.video} accent="var(--coral)">
                            <div
                                className="relative w-full no-print"
                                style={{ paddingBottom: "56.25%" }}
                            >
                                <iframe
                                    className="absolute inset-0 w-full h-full border-2 border-[var(--border)]"
                                    src={`https://www.youtube.com/embed/${week.video.youtube_id}`}
                                    title={week.video.title}
                                    allowFullScreen
                                    data-testid="week-video"
                                />
                            </div>
                            <p className="font-mono text-xs text-[var(--muted)] mt-3">
                                {week.video.title}
                            </p>
                            <button
                                type="button"
                                onClick={() => setReportOpen(true)}
                                data-testid="report-video"
                                className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--muted)] hover:text-[var(--coral)] inline-flex items-center gap-1 mt-2 no-print"
                            >
                                <Flag size={12} /> {t.student?.reportVideo || "Report this video"}
                            </button>
                        </Section>
                    )}

                    <Section Icon={Target} title={t.week.objectives} accent="var(--lime)">
                        <ul className="space-y-2">
                            {week.objectives.map((o, i) => (
                                <li key={i} className="font-mono text-[var(--text)] flex gap-3">
                                    <span className="text-[var(--lime)]">{`0${i + 1}.`}</span>
                                    {o}
                                </li>
                            ))}
                        </ul>
                    </Section>

                    <Section Icon={Hammer} title={t.week.activities} accent="var(--cyan)">
                        <ul className="space-y-2 list-disc list-inside font-mono text-[var(--text)]">
                            {week.activities.map((a, i) => (
                                <li key={i}>{a}</li>
                            ))}
                        </ul>
                    </Section>

                    <Section Icon={Package} title={t.week.materials} accent="var(--coral)">
                        <div className="flex flex-wrap gap-2">
                            {week.materials.map((m, i) => (
                                <span
                                    key={i}
                                    className="font-mono text-xs uppercase tracking-[0.14em] border border-[var(--border)] px-3 py-1.5 text-[var(--text)]"
                                >
                                    {m}
                                </span>
                            ))}
                        </div>
                    </Section>

                    <Section Icon={BookMarked} title={t.week.vocab} accent="var(--lime)">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {week.vocab.map((v, i) => (
                                <div key={i} className="border border-[var(--border)] p-4">
                                    <div className="font-display font-bold text-white">
                                        {v.term}
                                    </div>
                                    <p className="font-mono text-sm text-[var(--muted)] mt-1 leading-relaxed">
                                        {v.def}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </Section>

                    <Section Icon={MessagesSquare} title={t.week.discussion} accent="var(--cyan)">
                        <ul className="space-y-3">
                            {week.discussion.map((d, i) => (
                                <li
                                    key={i}
                                    className="font-mono text-[var(--text)] border-l-2 border-[var(--cyan)] pl-4"
                                >
                                    {d}
                                </li>
                            ))}
                        </ul>
                    </Section>

                    <Section Icon={Trophy} title={t.week.quiz} accent="var(--coral)">
                        <div className="space-y-6" data-testid="quiz-block">
                            {week.quiz.map((q, qi) => (
                                <div key={qi} data-testid={`quiz-q-${qi}`}>
                                    <p className="font-mono text-white mb-3">
                                        <span className="text-[var(--coral)]">{`Q${qi + 1}.`}</span>{" "}
                                        {q.q}
                                    </p>
                                    <div className="space-y-2">
                                        {q.options.map((opt, oi) => {
                                            const selected = answers[qi] === oi;
                                            let cls = "quiz-opt";
                                            if (result) {
                                                const correctIdx = result.correct_answers[qi];
                                                if (oi === correctIdx) cls += " correct";
                                                else if (selected && oi !== correctIdx)
                                                    cls += " wrong";
                                            } else if (selected) {
                                                cls +=
                                                    " border-[var(--cyan)] bg-[rgba(0,240,255,0.08)] text-white";
                                            }
                                            return (
                                                <button
                                                    key={oi}
                                                    disabled={!!result}
                                                    onClick={() => handleSelect(qi, oi)}
                                                    className={cls}
                                                    data-testid={`quiz-q${qi}-opt${oi}`}
                                                >
                                                    <span className="text-[var(--muted)] mr-2">
                                                        {String.fromCharCode(65 + oi)}.
                                                    </span>
                                                    {opt}
                                                    {result &&
                                                        oi === result.correct_answers[qi] && (
                                                            <CheckCircle2
                                                                size={16}
                                                                className="inline ml-2"
                                                            />
                                                        )}
                                                    {result &&
                                                        selected &&
                                                        oi !== result.correct_answers[qi] && (
                                                            <XCircle
                                                                size={16}
                                                                className="inline ml-2"
                                                            />
                                                        )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}

                            {!result && (
                                <button
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    className="btn-prime mt-2 no-print"
                                    data-testid="quiz-submit"
                                >
                                    {submitting ? t.week.scoring : t.week.submit}
                                </button>
                            )}
                            {result && (
                                <div className="terminal p-5 mt-4" data-testid="quiz-result">
                                    <p className="font-mono text-[var(--lime)] text-lg">
                                        {`> ${t.week.score}: ${result.score} / ${result.total}`}
                                    </p>
                                    <p className="font-mono text-[var(--muted)] text-sm mt-1">
                                        {result.score === result.total
                                            ? t.week.perfect
                                            : result.score >= result.total - 1
                                              ? t.week.close
                                              : t.week.again}
                                    </p>
                                </div>
                            )}
                        </div>
                    </Section>
                </div>

                <div className="mt-16 flex items-center justify-between gap-4 flex-wrap no-print">
                    {prevWeek ? (
                        <Link to={`/curriculum/${prevWeek}`} className="btn-ghost" data-testid="prev-week">
                            <ArrowLeft size={14} /> {t.week.prev}
                        </Link>
                    ) : (
                        <span />
                    )}
                    {nextWeek ? (
                        <Link to={`/curriculum/${nextWeek}`} className="btn-prime" data-testid="next-week">
                            {t.week.next} <ArrowRight size={14} />
                        </Link>
                    ) : (
                        <Link to="/curriculum" className="btn-coral" data-testid="finish-program">
                            {t.week.done}
                        </Link>
                    )}
                </div>
            </div>
            {reportOpen && week.video && (
                <ReportVideoModal
                    week={week}
                    onClose={() => setReportOpen(false)}
                />
            )}
        </main>
    );
}

function ReportVideoModal({ week, onClose }) {
    const { t } = useLang();
    const [note, setNote] = useState("");
    const [url, setUrl] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await submitVideoFeedback({
                week_id: week.id,
                youtube_id: week.video.youtube_id,
                kind: url.trim() ? "suggest" : "report",
                note,
                suggested_url: url,
            });
            setDone(true);
            setTimeout(onClose, 1500);
        } catch (_) {
            setDone(true);
            setTimeout(onClose, 1200);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4 no-print"
            onClick={onClose}
            data-testid="report-video-modal"
        >
            <div
                className="terminal max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--coral)]">
                        // FLAG VIDEO · {week.video.youtube_id}
                    </span>
                    <button onClick={onClose} className="text-[var(--muted)] hover:text-white">
                        <X size={16} />
                    </button>
                </div>
                {done ? (
                    <p
                        className="font-mono text-[var(--lime)]"
                        data-testid="report-video-thanks"
                    >
                        {t.student?.videoThanks || "Thanks — teachers will review."}
                    </p>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">
                                {t.student?.videoReport || "What's the issue?"}
                            </span>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={3}
                                placeholder={t.student?.videoReportPh || "broken link, off-topic..."}
                                className="input-line mt-1 resize-none"
                                data-testid="report-video-note"
                                maxLength={600}
                                required
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">
                                {t.student?.videoSuggest || "Suggest a better URL (optional)"}
                            </span>
                            <input
                                type="url"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://www.youtube.com/watch?v=..."
                                className="input-line mt-1"
                                data-testid="report-video-url"
                            />
                        </label>
                        <button
                            type="submit"
                            disabled={submitting || !note.trim()}
                            className="btn-coral w-full justify-center"
                            data-testid="report-video-submit"
                        >
                            {submitting ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Send size={14} />
                            )}
                            {t.student?.videoSubmit || "Send feedback"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

function Section({ Icon, title, accent, children }) {
    return (
        <section className="mt-12">
            <div className="flex items-center gap-3 mb-5">
                <Icon size={18} style={{ color: accent }} />
                <h2
                    className="font-display font-bold uppercase text-xl tracking-tight"
                    style={{ color: accent }}
                >
                    {title}
                </h2>
                <span className="flex-1 h-px bg-[var(--border)]" />
            </div>
            {children}
        </section>
    );
}
